import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Tracking } from '@freelancer/tracking';
import { Observable } from 'rxjs/Observable';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ABTEST_CONFIG } from './abtest.config';
import { ABTestConfig } from './abtest.interface';
import {
  ProjectExperiments,
  SessionExperiments,
  UserExperiments,
} from './experiments';

export enum ABTestType {
  SESSION_BASED = 1,
  USER_BASED = 2,
  PROJECT_BASED = 3,
}

export interface ABTestApiResponse<T extends keyof Experiments> {
  status: string;
  result: {
    state: 'in_test';
    test_id: number;
    variation_name: Experiments[T];
  };
}

export type VariationName = string;

export const STORAGE_KEY = 'WEBAPP_ABTEST_CACHE_V1';

type Experiments = SessionExperiments & UserExperiments & ProjectExperiments;

@Injectable()
export class ABTest {
  constructor(
    private http: HttpClient,
    private auth: Auth,
    private tracking: Tracking,
    private route: ActivatedRoute,
    @Inject(ABTEST_CONFIG) private config: ABTestConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  getSessionExperimentVariation<T extends keyof SessionExperiments>(
    experimentName: T,
  ): Promise<SessionExperiments[T]> {
    return this.tracking
      .getSessionId()
      .pipe(
        take(1),
        switchMap(sessionId =>
          this.getVariation(
            ABTestType.SESSION_BASED,
            experimentName,
            sessionId,
          ),
        ),
      )
      .toPromise();
  }

  // userId is optional, as it'll default to the user's own id
  getUserExperimentVariation<T extends keyof UserExperiments>(
    experimentName: T,
    userId?: number,
  ): Promise<UserExperiments[T]> {
    const userIdObservable = userId
      ? Observable.of(userId)
      : this.auth.getUserId().pipe(map(uid => parseInt(uid, 10)));
    return userIdObservable
      .pipe(
        take(1),
        switchMap(uid =>
          this.getVariation(ABTestType.USER_BASED, experimentName, uid),
        ),
      )
      .toPromise();
  }

  getProjectExperimentVariation<T extends keyof ProjectExperiments>(
    experimentName: T,
    projectId: number,
  ): Promise<ProjectExperiments[T]> {
    return this.getVariation(
      ABTestType.PROJECT_BASED,
      experimentName,
      projectId,
    );
  }

  private getVariation<T extends keyof Experiments>(
    experimentType: ABTestType,
    experimentName: T,
    identifier: string | number,
  ): Promise<Experiments[T]> {
    // test overrides functionality
    if (this.route.snapshot.queryParams.overrides) {
      const overrides = (<string>this.route.snapshot.queryParams.overrides)
        .split(',')
        .reduce<{ [experimentName: string]: Experiments[T] }>(
          (acc, experiment) => {
            const [name, variation] = experiment.split(':');
            acc[name] = <Experiments[T]>variation;
            return acc;
          },
          {},
        );
      if (overrides[experimentName]) {
        return Promise.resolve(overrides[experimentName]);
      }
    }
    // try reading the variation from cache if it exists
    const cacheKey = `${STORAGE_KEY}|${experimentType}|${experimentName}|${identifier}`;
    // skip cache when on the server
    if (isPlatformBrowser(this.platformId)) {
      let variationFromStorage: Experiments[T] | undefined;
      try {
        variationFromStorage =
          <Experiments[T]>window.sessionStorage.getItem(cacheKey) || '';
      } catch (e) {
        // ignore the errors, e.g. quota is full or security error
        console.error(e);
      }
      if (variationFromStorage) {
        return new Promise(resolve => resolve(variationFromStorage));
      }
    }
    // overwise get the variation for the A/B test server
    return this.auth
      .getAuthorizationHeader()
      .pipe(
        take(1),
        switchMap(authHeader =>
          this.http.post<ABTestApiResponse<T>>(
            `${this.config.apiBaseUrl}/abtest/0.1/enrollments/`,
            {
              id_type: experimentType,
              experiment_name: experimentName,
              id: identifier,
            },
          ),
        ),
        map(response => response.result.variation_name),
        tap(variationName => {
          // skip cache when on the server
          if (isPlatformBrowser(this.platformId)) {
            try {
              window.sessionStorage.setItem(cacheKey, variationName);
            } catch (e) {
              // ignore the errors, e.g. quota is full or security error
              console.error(e);
            }
          }
        }),
      )
      .toPromise()
      .catch((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          throw err.error;
        } else {
          // The backend returned an unsuccessful response code.
          throw new Error(err.error.message);
        }
      });
  }
}



// WEBPACK FOOTER //
// ./src/@freelancer/abtest/abtest.service.ts